/* eslint-disable @typescript-eslint/no-restricted-imports */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import type {
  DtoV1EnumOccupancyType,
  DtoV1SubjectPropertySaveSubjectPropertyInfoRequest,
  DtoV1SubjectPropertySubjectPropertySummaryResponse,
  UWMCLibraryCommonDomainEnumsPropertyType,
} from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import { CREDIT, US_STATES_LABEL_VALUE_ARRAY } from '~/common/constants';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import FormBuilder, {
  type FormBuilderField,
} from '~/pages/Loan1003DataGrids/common/components/FormBuilder';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';

import {
  INVALID_ACREAGE_MESSAGE,
  buildAppraisalInformationSection,
  isValidAcreField,
  isValidAppraisalFileId,
} from './AppraisalInformationSection';
import EscrowHoldbackFields from './EscrowHoldbackFields';
import ProposedPaymentAndRentalInformationSection from './ProposedPaymentAndRentalInformationSection';
import UnitsFieldArray from './UnitsFieldArray';
import {
  CONDO_PROPERTY_TYPE_VALUES,
  HOA_DUES_WARNING_MESSAGE,
  MANUFACTURED_PROPERTY_TYPE_VALUES,
  MANUFACTURED_PUD_VALUES,
  NUMBER_OF_UNITS_OPTIONS,
  PROPERTY_ATTACHMENT_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  PROPERTY_TYPE_OPTIONS_BY_KEYS,
  PUD_PROPERTY_TYPE_VALUES,
  REFINANCE_PURPOSE_TYPES,
  SUBJECT_PROPERTY_OCCUPANCY_OPTIONS,
  isHoaConditionHidden,
} from './constants';
import { getCondoInformationFields } from './getCondoInformationFields';
import { getManufacturedHomeFields } from './getManufacturedHomeFields';
import { getPUDInformationFields } from './getPUDInformationFields';

type SubjectPropertySummariesFormProps = {
  rowToEdit: DtoV1SubjectPropertySubjectPropertySummaryResponse & {
    isGrossMonthlyRentReadOnly?: boolean;
  };
  onClose: () => void;
};

type SizeMeasurementOption = 'squareFootage' | 'numberOfAcres';

const toNumberOrNull = (value: unknown) => {
  if (value === '' || value == null) {
    return null;
  }

  return Number(value);
};

const isManufactured = (
  propertyType: UWMCLibraryCommonDomainEnumsPropertyType,
): boolean => MANUFACTURED_PROPERTY_TYPE_VALUES.has(String(propertyType));

const isPud = (
  propertyType: UWMCLibraryCommonDomainEnumsPropertyType,
): boolean => PUD_PROPERTY_TYPE_VALUES.has(String(propertyType));

const isCondo = (
  propertyType: UWMCLibraryCommonDomainEnumsPropertyType,
): boolean => CONDO_PROPERTY_TYPE_VALUES.has(String(propertyType));

const isCooperative = (
  propertyType: UWMCLibraryCommonDomainEnumsPropertyType,
): boolean =>
  String(propertyType) === PROPERTY_TYPE_OPTIONS_BY_KEYS.Cooperative;

const intFlagToNullableBoolean = (value: number | null) =>
  value == null ? null : value === 1;

const getDefaultValues = (
  data: DtoV1SubjectPropertySubjectPropertySummaryResponse,
) => {
  const hasMeasurementValue = (value: unknown) => {
    if (value == null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return true;
  };

  const hasSquareFootage = hasMeasurementValue(data.squareFootage);
  const hasNumberOfAcres = hasMeasurementValue(data.numberOfAcres);
  const defaultSizeMeasurement: SizeMeasurementOption =
    hasNumberOfAcres && !hasSquareFootage ? 'numberOfAcres' : 'squareFootage';

  return {
    addressLine1: data.propertyAddress?.addressLine1 ?? '',
    unitNo: data.propertyAddress?.unit ?? '',
    city: data.propertyAddress?.city ?? '',
    county: data.propertyAddress?.county ?? '',
    state: data.propertyAddress?.state ?? '',
    zipCode: data.propertyAddress?.zipCode ?? '',
    parcelId: data.parcelId ?? '',
    occupancyType:
      data.occupancyType ?? SUBJECT_PROPERTY_OCCUPANCY_OPTIONS[0].value,
    appraisedValue: data.appraisedValue ?? null,
    netRent: data.totalNetRentalIncome ?? null,
    currentAddressId: data.currentAddressId ?? null,
    yearBuilt: data.yearBuilt ?? null,
    fipsCountyCode: data.fipsCountyCode ?? '',
    numberOfUnits: data.numberOfUnits ?? null,
    squareFootage: data.squareFootage ?? null,
    numberOfAcres: data.numberOfAcres ?? null,
    sizeMeasurement: defaultSizeMeasurement,
    propertyType: data.propertyType ?? null,
    attachedPropertyType: data.attachedPropertyType ?? null,
    propertyValueFormType: data.propertyValueFormType ?? null,
    propertyReviewFormNum: data.propertyReviewFormNum ?? null,
    appraisalFileIdNum: data.appraisalFileIdNum ?? null,
    buildingStatus: data.buildingStatus ?? null,
    rentalIncome: data.rentalIncome ?? null,
    percentRentalIncome: data.percentRentalIncome ?? null,
    rentalIncomeDocTypeId: data.rentalIncomeDocTypeId ?? null,
    adjustedRentalIncome: data.adjustedRentalIncome ?? null,
    comments: data.comments ?? '',
    mixedUseProperty: data.mixedUseProperty ?? false,
    ruralProperty: data.ruralProperty ?? false,
    units: (data.units ?? []).map((u) => ({
      numberOfBedrooms: u.numberOfBedrooms ?? null,
      grossMonthlyRents: u.grossMonthlyRents ?? null,
    })),
    monthlyHOADues: data.monthlyHOADues ?? null,
    verifiedByUnderwriter: data.verifiedByUnderwriter ?? false,
    expectedRepairsCompletionDate: data.expectedRepairsCompletionDate ?? null,
    assuranceCompletionAmount: data.assuranceCompletionAmount ?? null,
    sellerEscrowHoldbackAmount: data.sellerEscrowHoldbackAmount ?? null,
    requiredImprovements: data.requiredImprovements ?? '',
    condoProjectName: data.propertyName ?? '',
    cpmProjectId: data.cpmProjectId ?? '',
    hoaTaxId: data.homeownersAssocEin ?? '',
    phaseId: data.phaseId ?? '',
    fannieCpmCertificationId: data.fannieCpmCertificationId ?? '',
    projectAssessmentsResultsNum: data.projectAssessmentsResultsNum ?? '',
    condoProjectWaiverRequestNum: data.condoProjectWaiverRequestNum ?? '',
    condoProjectDesignType: data.projectDesignTypeId ?? null,
    condoFreddieMacClass:
      isCondo(data.propertyType) || isCooperative(data.propertyType)
        ? data.classification
        : null,
    pudClass: isPud(data.propertyType) ? data.classification : null,
    fannieMaeClass: data.classification3 ?? null,
    condoNumberOfUnits: data.numberOfCondoUnits ?? null,
    condoNumberOfUnitsSold: data.numberOfUnitsSold ?? null,
    condoPredominantlyDetached: intFlagToNullableBoolean(
      data.condoProjDetachedFlag,
    ),
    condoWarrantable: data.warrantable ?? false,
    condoEditExpirationDate: intFlagToNullableBoolean(
      data.isQuestionnaireExpirationDateOverriden,
    ),
    condoQuestionnaireEffectiveDate: data.questionnaireEffectiveDate ?? null,
    condoMasterInsuranceStartDate: data.masterInsuranceEffectiveDate ?? null,
    condoBudgetStartDate: data.budgetEffectiveDate ?? null,
    condoQuestionnaireExpirationDate: data.questionnaireExpirationDate ?? null,
    condoMasterInsuranceEndDate: data.masterInsuranceExpirationDate ?? null,
    condoBudgetEndDate: data.budgetExpirationDate ?? null,
    lengthInFeet: data.lengthInFeet ?? null,
    widthInFeet: data.widthInFeet ?? null,
    serialNumber: data.serialNumber ?? '',
    model: data.model ?? '',
    hudLabelNumber: data.hudLabelNumber ?? '',
    make: data.make ?? '',
    certOfTitleType: data.certOfTitleType ?? '',
    newOrUsedIndicator: data.newOrUsedIndicator ?? null,
    mobileHomeParkFlag: data.mobileHomeParkFlag ?? null,
    attachToFoundationFlag: data.attachToFoundationFlag ?? null,
    isRentalIncomeCalculatorCompleted:
      data.isRentalIncomeCalculatorCompleted ?? false,
    coopMaintenanceFee: data.coopMaintenanceFee ?? null,
  };
};

export default function SubjectPropertySummariesForm({
  rowToEdit,
  onClose,
}: Readonly<SubjectPropertySummariesFormProps>) {
  const DEFAULT_RENTAL_INCOME_PERCENT = 75;

  const { isReadOnly, obfuscatedLoanIdentifier } = useUrl();
  const { data: loanSummary } = useGetLoanSummary();
  const isWriteAccessDisabled = useIsWriteAccessDisabled() || isReadOnly;
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const [showEscrowFields, setShowEscrowFields] = useState(
    rowToEdit.assuranceOfCompletion === 'Y' ||
      !!rowToEdit.expectedRepairsCompletionDate ||
      !!rowToEdit.assuranceCompletionAmount ||
      !!rowToEdit.sellerEscrowHoldbackAmount ||
      !!rowToEdit.requiredImprovements,
  );

  const isRefinance = (REFINANCE_PURPOSE_TYPES as readonly number[]).includes(
    rowToEdit.purposeType,
  );

  const {
    control,
    getValues,
    setValue,
    watch,
    register,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: getDefaultValues(rowToEdit),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const watchedAddress = watch('addressLine1');

  const handleChange = (field: string, value: unknown) => {
    setValue(field, value);
  };

  const handleAddEscrow = () => {
    setShowEscrowFields(true);
  };

  const handleRemoveEscrow = () => {
    setShowEscrowFields(false);
    setValue('assuranceCompletionAmount', null);
    setValue('sellerEscrowHoldbackAmount', null);
    setValue('requiredImprovements', '');
  };

  const handleAssociatedReoChange = (value: unknown) => {
    handleChange('currentAddressId', value);
    const selectedReo = (rowToEdit.associatedReos ?? []).find(
      (reo) => reo.addressId === +value,
    );
    if (selectedReo) {
      setValue('currentAddressId', selectedReo.addressId ?? null);
      setValue('addressLine1', selectedReo.addressLine1 ?? '');
      setValue('unitNo', selectedReo.unit ?? '');
      setValue('city', selectedReo.city ?? '');
      setValue('county', selectedReo.county ?? '');
      setValue('state', selectedReo.state ?? '');
      setValue('zipCode', selectedReo.zipCode ?? '');
    }
  };

  const { mutate: saveSubjectPropertyInfo, isLoading } = useMutation<
    void,
    unknown,
    DtoV1SubjectPropertySaveSubjectPropertyInfoRequest
  >({
    mutationFn: async (request) => {
      await typedApiClient.v1.subjectPropertySaveSubjectPropertyInfo(request);
    },
    onSuccess: () => {
      showSnackbar({
        severity: 'success',
        message: 'Saved Successfully',
        duration: 5000,
      });
      queryClient.invalidateQueries([
        'subjectPropertySummary',
        obfuscatedLoanIdentifier,
      ]);
      onClose();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: 'Record was not able to be saved.',
        duration: 5000,
      });
    },
  });

  const handleSave = async () => {
    const isValid = await trigger('appraisalFileIdNum');
    if (!isValid) {
      return;
    }

    const getEmptyValueForType = (type: string) => {
      if (type === 'checkbox') return false;
      if (type === 'text') return '';
      return null;
    };

    const clearHiddenFields = () => {
      const sectionsToCheck = [
        {
          fields: propertyCharacteristicsSection,
          sectionVisible: true,
        },
        {
          fields: condoInformationFields,
          sectionVisible: isCondoPropertyType || isCooperativePropertyType,
        },
        { fields: pudInformationFields, sectionVisible: isPUDPropertyType },
        {
          fields: manufacturedHomeFields,
          sectionVisible: isManufacturedPropertyType,
        },
      ];

      // some fields (e.g. condoProjectName, cpmProjectId, hoaTaxId) are shared between sections
      const visibleFieldNames = new Set<string>();
      const hiddenFields: Array<{ name: string; type: string }> = [];

      for (const { fields, sectionVisible } of sectionsToCheck) {
        for (const field of fields) {
          const { name, type, isHidden } = field as FormBuilderField;
          if (sectionVisible && !isHidden?.()) {
            visibleFieldNames.add(name);
          } else {
            hiddenFields.push({ name, type });
          }
        }
      }

      for (const { name, type } of hiddenFields) {
        if (!visibleFieldNames.has(name)) {
          setValue(name, getEmptyValueForType(type));
        }
      }
    };

    clearHiddenFields();

    const selectedPropertyType = String(getValues('propertyType') ?? '');
    const requiresHoaTaxIdValidation =
      CONDO_PROPERTY_TYPE_VALUES.has(selectedPropertyType) ||
      selectedPropertyType === PROPERTY_TYPE_OPTIONS_BY_KEYS.Cooperative ||
      PUD_PROPERTY_TYPE_VALUES.has(selectedPropertyType);

    const isHoaTaxIdValid = requiresHoaTaxIdValidation
      ? await trigger('hoaTaxId')
      : true;
    if (!isHoaTaxIdValid) {
      return;
    }

    const values = getValues();
    if (showEscrowFields) {
      const escrowAmountFields = isRefinance
        ? ['assuranceCompletionAmount']
        : ['assuranceCompletionAmount', 'sellerEscrowHoldbackAmount'];
      const isEscrowValid = await trigger(escrowAmountFields);
      if (!isEscrowValid) {
        return;
      }
    }

    const selectedSizeMeasurement = values.sizeMeasurement;
    const squareFootageToSave =
      selectedSizeMeasurement === 'squareFootage'
        ? (values.squareFootage ?? null)
        : null;
    const numberOfAcresToSave =
      selectedSizeMeasurement === 'numberOfAcres'
        ? (values.numberOfAcres ?? null)
        : null;

    saveSubjectPropertyInfo({
      obfuscatedLoanIdentifier,
      propertyAddress: {
        unit: values.unitNo || '',
        addressLine1: values.addressLine1 || '',
        city: values.city || '',
        county: values.county || '',
        state: values.state || '',
        zipCode: values.zipCode || '',
      },
      parcelId: values.parcelId || null,
      occupancyType: +values.occupancyType as DtoV1EnumOccupancyType,
      mixedUseProperty: values.mixedUseProperty,
      ruralProperty: values.ruralProperty,
      currentAddressId: values.currentAddressId ?? null,
      appraisedValue: values.appraisedValue ?? null,
      totalNetRentalIncome: values.netRent ?? null,
      yearBuilt: values.yearBuilt ?? null,
      numberOfUnits: values.numberOfUnits ?? null,
      squareFootage: squareFootageToSave,
      numberOfAcres: numberOfAcresToSave,
      propertyType: values.propertyType ?? null,
      attachedPropertyType: values.attachedPropertyType ?? null,
      propertyValueFormType: values.propertyValueFormType ?? null,
      propertyReviewFormNum: values.propertyReviewFormNum || null,
      appraisalFileIdNum: values.appraisalFileIdNum || null,
      buildingStatus: values.buildingStatus || null,
      rentalIncome: values.rentalIncome ?? null,
      percentRentalIncome: values.percentRentalIncome ?? null,
      rentalIncomeDocTypeId: values.rentalIncomeDocTypeId
        ? +values.rentalIncomeDocTypeId
        : null,
      adjustedRentalIncome: values.adjustedRentalIncome ?? null,
      comments: values.comments || null,
      units: (values.units ?? []).map((unit, index) => ({
        numberOfBedrooms: unit.numberOfBedrooms ?? null,
        grossMonthlyRents: unit.grossMonthlyRents ?? null,
        unitNumber: String(index + 1),
      })),
      monthlyHOADues: values.monthlyHOADues ?? null,
      verifiedByUnderwriter: values.verifiedByUnderwriter ?? false,
      assuranceOfCompletion: showEscrowFields ? 'Y' : null,
      assuranceCompletionAmount: showEscrowFields
        ? (values.assuranceCompletionAmount ?? null)
        : null,
      sellerEscrowHoldbackAmount: showEscrowFields
        ? (values.sellerEscrowHoldbackAmount ?? null)
        : null,
      requiredImprovements: showEscrowFields
        ? values.requiredImprovements || null
        : null,
      propertyName: values.condoProjectName ?? '',
      cpmProjectId: values.cpmProjectId ?? '',
      homeownersAssocEin: values.hoaTaxId ?? '',
      phaseId: values.phaseId ?? '',
      fannieCpmCertificationId: values.fannieCpmCertificationId ?? '',
      projectAssessmentsResultsNum: values.projectAssessmentsResultsNum ?? '',
      condoProjectWaiverRequestNum: values.condoProjectWaiverRequestNum ?? '',
      projectDesignTypeId: values.condoProjectDesignType ?? null,
      classification: (values.condoFreddieMacClass || values.pudClass) ?? null,
      classification3: values.fannieMaeClass ?? null,
      numberOfCondoUnits: values.condoNumberOfUnits ?? null,
      numberOfUnitsSold: values.condoNumberOfUnitsSold ?? null,
      condoProjDetachedFlag: toNumberOrNull(values.condoPredominantlyDetached),
      warrantable: Boolean(values.condoWarrantable ?? ''),
      isQuestionnaireExpirationDateOverriden: toNumberOrNull(
        values.condoEditExpirationDate,
      ),
      questionnaireEffectiveDate:
        values.condoQuestionnaireEffectiveDate ?? null,
      masterInsuranceEffectiveDate:
        values.condoMasterInsuranceStartDate ?? null,
      budgetEffectiveDate: values.condoBudgetStartDate ?? null,
      questionnaireExpirationDate:
        values.condoQuestionnaireExpirationDate ?? null,
      masterInsuranceExpirationDate: values.condoMasterInsuranceEndDate ?? null,
      budgetExpirationDate: values.condoBudgetEndDate ?? null,
      lengthInFeet: values.lengthInFeet ?? null,
      widthInFeet: values.widthInFeet ?? null,
      serialNumber: values.serialNumber || null,
      model: values.model || null,
      hudLabelNumber: values.hudLabelNumber || null,
      make: values.make || null,
      certOfTitleType: values.certOfTitleType || null,
      newOrUsedIndicator: toNumberOrNull(values.newOrUsedIndicator),
      mobileHomeParkFlag: toNumberOrNull(values.mobileHomeParkFlag),
      attachToFoundationFlag: toNumberOrNull(values.attachToFoundationFlag),
      coopMaintenanceFee: values.coopMaintenanceFee ?? null,
    });
  };

  const associatedReoOptions = (rowToEdit.associatedReos ?? []).map((reo) => ({
    value: reo?.addressId,
    label: reo.addressLine1 ?? '',
  }));

  const addressInfoSection = [
    {
      type: 'text',
      name: 'addressLine1',
      label: 'Address',
      dataTestId: 'addressLine1',
      placeholder: 'Address',
      span: 2,
    },
    {
      type: 'text',
      name: 'unitNo',
      label: 'Unit #',
      dataTestId: 'unitNo',
      placeholder: 'Unit #',
    },
    {
      type: 'text',
      name: 'city',
      label: 'City',
      dataTestId: 'city',
      placeholder: 'City',
      span: 2,
    },
    {
      type: 'text',
      name: 'county',
      label: 'County',
      dataTestId: 'county',
      placeholder: 'County',
      span: 2,
    },
    {
      type: 'select',
      name: 'state',
      label: 'State',
      dataTestId: 'state',
      options: US_STATES_LABEL_VALUE_ARRAY,
      onChangeHandler: (value) => handleChange('state', value),
    },
    {
      type: 'text',
      name: 'zipCode',
      label: 'ZIP',
      dataTestId: 'zipCode',
      placeholder: 'ZIP',
      pattern: '^[0-9]{0,5}(-[0-9]{0,4})?$',
    },
    {
      type: 'text',
      name: 'parcelId',
      label: 'Parcel ID',
      dataTestId: 'parcelId',
      placeholder: 'Parcel ID',
      span: 3,
    },
    {
      type: 'select',
      name: 'occupancyType',
      label: 'Occupancy',
      dataTestId: 'occupancyType',
      options: SUBJECT_PROPERTY_OCCUPANCY_OPTIONS,
      onChangeHandler: (value) => handleChange('occupancyType', +value),
      span: 2,
    },
    {
      type: 'select',
      name: 'currentAddressId',
      label: 'Associated REO',
      dataTestId: 'currentAddressId',
      placeholder: ' ',
      optionsAttributes: { optionsLabelName: 'value', optionsValueName: 'key' },
      options: associatedReoOptions,
      onChangeHandler: (value) => handleAssociatedReoChange(value),
      span: 2,
    },
    {
      type: 'checkbox',
      name: 'mixedUseProperty',
      value: 'mixedUseProperty',
      label: 'Mixed Use Property',
      dataTestId: 'mixedUseProperty',
      span: 1,
      useHooksForm: true,
    },
    {
      type: 'checkbox',
      name: 'ruralProperty',
      value: 'ruralProperty',
      label: 'Rural Property',
      dataTestId: 'ruralProperty',
      useHooksForm: true,
    },
  ];

  register('yearBuilt');
  register('monthlyHOADues');
  register('coopMaintenanceFee');
  register('requiredImprovements');
  watch('requiredImprovements');
  register('condoNumberOfUnits');
  register('condoNumberOfUnitsSold');
  register('sizeMeasurement');
  register('appraisalFileIdNum');
  register('squareFootage');
  register('numberOfAcres', {
    validate: (value: number) =>
      getValues('sizeMeasurement') !== 'numberOfAcres' ||
      isValidAcreField(value) ||
      INVALID_ACREAGE_MESSAGE,
  });
  register('newOrUsedIndicator');
  register('mobileHomeParkFlag');
  register('attachToFoundationFlag');
  watch('newOrUsedIndicator');
  watch('mobileHomeParkFlag');
  watch('attachToFoundationFlag');
  const sizeMeasurement = watch('sizeMeasurement');
  const propertyType = watch('propertyType');
  const watchedNumberOfUnits = watch('numberOfUnits');
  const hoaDues = watch('monthlyHOADues');
  const coopMaintenanceFee = watch('coopMaintenanceFee');
  const isVerifiedByUnderwriter = watch('verifiedByUnderwriter');
  const editExpirationDate = watch('condoEditExpirationDate');
  const occupancyType = watch('occupancyType');
  const grossMonthlyRent = watch('rentalIncome');
  const percentRentalIncome = watch('percentRentalIncome');
  const adjustedMonthlyGrossIncome = watch('adjustedRentalIncome');
  const netRentalIncome = watch('netRent');

  const isTwoToFourUnitPrimaryResidence =
    Number(propertyType) ===
      Number(PROPERTY_TYPE_OPTIONS_BY_KEYS['2-4 Unit Dwelling']) &&
    Number(occupancyType) === SUBJECT_PROPERTY_OCCUPANCY_OPTIONS[0].value;
  const isInvestmentProperty =
    Number(occupancyType) === SUBJECT_PROPERTY_OCCUPANCY_OPTIONS[2].value ||
    Number(occupancyType) === SUBJECT_PROPERTY_OCCUPANCY_OPTIONS[3].value;
  const isTwoToFourUnitNonPrimaryResidence =
    Number(propertyType) ===
      Number(PROPERTY_TYPE_OPTIONS_BY_KEYS['2-4 Unit Dwelling']) &&
    Number(occupancyType) !== SUBJECT_PROPERTY_OCCUPANCY_OPTIONS[0].value;

  const isGrossMonthlyRentEditableByLoanAttributes =
    isTwoToFourUnitPrimaryResidence || isInvestmentProperty;

  const isGrossMonthlyRentReadOnlyByCalculator = Boolean(
    rowToEdit.isGrossMonthlyRentReadOnly ?? false,
  );
  const isRentalIncomeCalculatorCompleted = Boolean(
    rowToEdit.isRentalIncomeCalculatorCompleted ?? false,
  );

  const isGrossMonthlyRentLockedByCalculator =
    isGrossMonthlyRentReadOnlyByCalculator || isRentalIncomeCalculatorCompleted;

  const isGrossMonthlyRentEditable =
    isGrossMonthlyRentEditableByLoanAttributes &&
    !isGrossMonthlyRentLockedByCalculator;

  const totalHousingExpensePiti =
    Number(rowToEdit.proposedPrincipalAndInterestPayment ?? 0) +
    Number(rowToEdit.proposedOtherFinancing ?? 0) +
    Number(rowToEdit.proposedMortgageInsurance ?? 0) +
    Number(rowToEdit.proposedHazardInsurance ?? 0) +
    Number(rowToEdit.proposedFloodInsurance ?? 0) +
    Number(rowToEdit.proposedRealEstateTaxes ?? 0);

  const calculateRentalIncomeValues = useCallback(
    (parsedGrossMonthlyRent: number) => {
      const rentalIncomePercent = Number(
        percentRentalIncome ??
          rowToEdit.percentRentalIncome ??
          DEFAULT_RENTAL_INCOME_PERCENT,
      );

      const adjustedIncome = Number(
        ((parsedGrossMonthlyRent * rentalIncomePercent) / 100).toFixed(2),
      );
      const shouldDeductPiti =
        isInvestmentProperty || isTwoToFourUnitNonPrimaryResidence;
      const netIncome = shouldDeductPiti
        ? Number((adjustedIncome - totalHousingExpensePiti).toFixed(2))
        : adjustedIncome;

      return { adjustedIncome, netIncome };
    },
    [
      percentRentalIncome,
      rowToEdit.percentRentalIncome,
      DEFAULT_RENTAL_INCOME_PERCENT,
      isInvestmentProperty,
      isTwoToFourUnitNonPrimaryResidence,
      totalHousingExpensePiti,
    ],
  );

  const handleGrossMonthlyRentChange = (value: string) => {
    if (isGrossMonthlyRentLockedByCalculator) {
      return;
    }

    const parsedGrossMonthlyRent = Number(value || 0);

    if (!value) {
      setValue('rentalIncome', null);
      setValue('adjustedRentalIncome', null);
      setValue('netRent', null);
      return;
    }

    const { adjustedIncome, netIncome } = calculateRentalIncomeValues(
      parsedGrossMonthlyRent,
    );

    setValue('rentalIncome', parsedGrossMonthlyRent);
    setValue('adjustedRentalIncome', adjustedIncome);
    setValue('netRent', netIncome);
  };

  useEffect(() => {
    if (isGrossMonthlyRentEditableByLoanAttributes) {
      return;
    }

    if (grossMonthlyRent !== 0) {
      setValue('rentalIncome', 0);
    }

    if (adjustedMonthlyGrossIncome !== 0) {
      setValue('adjustedRentalIncome', 0);
    }

    if (netRentalIncome !== 0) {
      setValue('netRent', 0);
    }
  }, [
    isGrossMonthlyRentEditableByLoanAttributes,
    grossMonthlyRent,
    adjustedMonthlyGrossIncome,
    netRentalIncome,
    setValue,
  ]);

  useEffect(() => {
    if (isGrossMonthlyRentLockedByCalculator) {
      return;
    }

    if (grossMonthlyRent == null) {
      return;
    }

    const parsedGrossMonthlyRent = Number(grossMonthlyRent);

    if (Number.isNaN(parsedGrossMonthlyRent)) {
      return;
    }

    const { adjustedIncome, netIncome } = calculateRentalIncomeValues(
      parsedGrossMonthlyRent,
    );

    setValue('adjustedRentalIncome', adjustedIncome);
    setValue('netRent', netIncome);
  }, [
    calculateRentalIncomeValues,
    grossMonthlyRent,
    setValue,
    isGrossMonthlyRentLockedByCalculator,
  ]);

  const isCondoPropertyType = isCondo(propertyType);
  const isPUDPropertyType = isPud(propertyType);
  const isManufacturedPropertyType = isManufactured(propertyType);
  const isCooperativePropertyType = isCooperative(propertyType);
  const showFannieMaeClassForManufactured = MANUFACTURED_PUD_VALUES.has(
    String(propertyType),
  );

  const condoInformationFields = getCondoInformationFields({
    watch,
    handleChange,
    editExpirationDate: editExpirationDate ? 1 : 0,
    isCooperative: isCooperativePropertyType,
    isLoanProductAdvisor:
      loanSummary?.loanInfo?.underwritingMethod === CREDIT.LOAN_PRODUCT_ADVISOR,
    isConventional: loanSummary?.loanInfo?.loanProductType === 'Conventional',
  });

  const pudInformationFields = getPUDInformationFields({
    handleChange,
  });

  const manufacturedHomeFields = getManufacturedHomeFields({
    watch,
    handleChange,
    showFannieMaeClass: showFannieMaeClassForManufactured,
  });

  const appraisalFileIdValue = watch('appraisalFileIdNum');
  const hasInvalidAppraisalFileId =
    !isValidAppraisalFileId(appraisalFileIdValue);
  const isAppraisedValueReadOnly = rowToEdit.isAppraisalEditable === false;

  const numberOfAcresValue = watch('numberOfAcres');
  const isAcreageInvalid =
    sizeMeasurement === 'numberOfAcres' &&
    !isValidAcreField(numberOfAcresValue);

  const propertyCharacteristicsSection: Array<
    FormBuilderField & Record<string, unknown>
  > = [
    {
      type: 'select',
      name: 'propertyType',
      label: 'Property Type',
      dataTestId: 'propertyType',
      options: PROPERTY_TYPE_OPTIONS,
      span: 2,
    },
    {
      type: 'select',
      name: 'attachedPropertyType',
      label: 'Property Attachment Type',
      dataTestId: 'attachedPropertyType',
      options: PROPERTY_ATTACHMENT_OPTIONS,
      span: 2,
    },
    {
      type: 'select',
      name: 'numberOfUnits',
      label: 'Number of Units',
      dataTestId: 'numberOfUnits',
      options: NUMBER_OF_UNITS_OPTIONS,
      span: 1,
    },
    {
      type: 'text',
      name: 'yearBuilt',
      label: 'Year Built',
      dataTestId: 'yearBuilt',
      value: watch('yearBuilt'),
      placeholder: 'Year Built',
      span: 1,
      pattern: '^[0-9]{0,4}$',
      onChangeHandler: (e) => {
        setValue('yearBuilt', e.target.value);
      },
    },
    {
      type: 'text',
      name: 'fipsCountyCode',
      label: 'FIPS County Code',
      dataTestId: 'fipsCountyCode',
      placeholder: 'FIPS County Code',
      readOnly: true,
      span: 1,
    },
    {
      type: 'currency',
      name: 'monthlyHOADues',
      label: 'Monthly HOA Dues',
      dataTestId: 'monthlyHOADues',
      placeholder: 'Monthly HOA Dues',
      onChangeHandler: (value) => handleChange('monthlyHOADues', value),
      value: hoaDues,
      span: 2,
      isHidden: () =>
        String(propertyType) === PROPERTY_TYPE_OPTIONS_BY_KEYS.Cooperative,
    },
    {
      type: 'currency',
      name: 'coopMaintenanceFee',
      label: 'Co-op Maintenance Fee',
      value: coopMaintenanceFee,
      dataTestId: 'coopMaintenanceFee',
      placeholder: 'Co-op Maintenance Fee',
      onChangeHandler: (value) => handleChange('coopMaintenanceFee', value),
      span: 2,
      isHidden: () =>
        String(propertyType) !== PROPERTY_TYPE_OPTIONS_BY_KEYS.Cooperative,
    },
    {
      type: 'checkbox',
      name: 'verifiedByUnderwriter',
      value: 'verifiedByUnderwriter',
      label: 'Verified by Underwriter',
      dataTestId: 'verifiedByUnderwriter',
      colStart: 8,
      span: 2,
      useHooksForm: true,
    },
    {
      type: 'alert',
      name: 'hoaDuesWarning',
      dataTestId: 'hoaDuesWarning',
      status: 'warning' as const,
      description: HOA_DUES_WARNING_MESSAGE,
      colStart: 1,
      span: 9,
      isHidden: () =>
        isHoaConditionHidden(
          String(propertyType),
          Boolean(isVerifiedByUnderwriter),
          Boolean(rowToEdit.hasPropertyInspectionWaiver),
          Number(hoaDues),
        ),
    },
    {
      type: 'divider',
      span: 9,
    },
  ];

  const appraisalInformationSection = buildAppraisalInformationSection({
    handleChange,
    setValue,
    trigger,
    sizeMeasurement,
    isAppraisedValueReadOnly,
    watch,
  });

  return (
    <Loan1003FormContainer
      onCloseClicked={onClose}
      onSaveClicked={handleSave}
      isLoading={isLoading}
      disableSave={
        !watchedAddress || hasInvalidAppraisalFileId || isAcreageInvalid
      }
      readOnlyMode={isWriteAccessDisabled}
    >
      <Loan1003FormSection title="Address Information">
        <FormBuilder
          fields={addressInfoSection}
          columns={9}
          columnGap="1rem"
          errors={errors}
          showErrors
          getValues={getValues}
          control={control}
          readOnlyMode={isWriteAccessDisabled}
        />
      </Loan1003FormSection>
      <Loan1003FormSection title="Property Characteristics">
        <FormBuilder
          fields={propertyCharacteristicsSection}
          columns={9}
          columnGap="1rem"
          errors={errors}
          showErrors
          getValues={getValues}
          control={control}
          readOnlyMode={isWriteAccessDisabled}
        />
        <UnitsFieldArray
          control={control}
          numberOfUnits={watchedNumberOfUnits}
          isWriteAccessDisabled={isWriteAccessDisabled}
        />
      </Loan1003FormSection>
      <Loan1003FormSection title="Appraisal Information">
        <FormBuilder
          fields={appraisalInformationSection}
          columns={9}
          columnGap="1rem"
          errors={errors}
          showErrors
          getValues={getValues}
          control={control}
          readOnlyMode={isWriteAccessDisabled}
        />
      </Loan1003FormSection>
      {(isCondoPropertyType || isCooperativePropertyType) && (
        <Loan1003FormSection
          title={
            isCooperativePropertyType
              ? 'Cooperative Information'
              : 'Condominium Information'
          }
        >
          <FormBuilder
            fields={condoInformationFields}
            columns={9}
            columnGap="1rem"
            errors={errors}
            getValues={getValues}
            control={control}
            showErrors
            readOnlyMode={isWriteAccessDisabled}
          />
        </Loan1003FormSection>
      )}
      {isPUDPropertyType && (
        <Loan1003FormSection title="PUD Information">
          <FormBuilder
            fields={pudInformationFields}
            columns={9}
            columnGap="1rem"
            errors={errors}
            getValues={getValues}
            control={control}
            showErrors
            readOnlyMode={isWriteAccessDisabled}
          />
        </Loan1003FormSection>
      )}
      {isManufacturedPropertyType && (
        <Loan1003FormSection title="Manufactured Housing Information">
          <FormBuilder
            fields={manufacturedHomeFields}
            columns={9}
            columnGap="1rem"
            errors={errors}
            getValues={getValues}
            control={control}
            showErrors
            readOnlyMode={isWriteAccessDisabled}
          />
        </Loan1003FormSection>
      )}
      <Loan1003FormSection title="Proposed Payment & Rental Information">
        <ProposedPaymentAndRentalInformationSection
          rowToEdit={rowToEdit}
          grossMonthlyRent={grossMonthlyRent ?? null}
          adjustedMonthlyGrossIncome={adjustedMonthlyGrossIncome ?? null}
          netRentalIncome={netRentalIncome ?? null}
          isGrossMonthlyRentEditable={isGrossMonthlyRentEditable}
          onGrossMonthlyRentChange={handleGrossMonthlyRentChange}
          isWriteAccessDisabled={isWriteAccessDisabled}
        />
      </Loan1003FormSection>
      <Loan1003FormSection title="Escrow Holdback">
        <EscrowHoldbackFields
          control={control}
          getValues={getValues}
          errors={errors}
          showFields={showEscrowFields}
          isRefinance={isRefinance}
          onAdd={handleAddEscrow}
          onRemove={handleRemoveEscrow}
          onChangeHandler={handleChange}
          trigger={trigger}
          isWriteAccessDisabled={isWriteAccessDisabled}
        />
      </Loan1003FormSection>
    </Loan1003FormContainer>
  );
}
